import React from "react";
import { Field } from "formik";
import PropTypes from "prop-types";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { autoCompleteInputStyles } from "..";
import _ from "lodash";
import AsyncSelect from "react-select/async";
import jssVariables from "../../../../jss_setup";
import { fetchPortAutocomplete } from "../../../../api";

async function portSearch({ query }) {
  var data = await fetchPortAutocomplete({
    name: _.trim(query),
    starts_with: "",
  });

  const ports = _.get(data, "ports", [])
    .filter((i, idx) => idx < 10)
    .map((port) => {
      const label = `${_.startCase(_.toLower(port.name))} - ${
        port.country_code
      }`;
      return {
        ...port,
        label,
        value: label,
        location_type: "port",
      };
    });

  return ports;
}

function promiseOptions(inputValue) {
  if (inputValue.length < 3) {
    return null;
  }
  return new Promise((resolve) => {
    const promises = [portSearch({ query: inputValue })];
    resolve(Promise.all(promises).then(([ports]) => [...(ports || [])]));
  });
}
const debouncedSearch = _.debounce((query, callback) => {
  if (query.length < 3) {
    callback([]);
    return;
  }

  promiseOptions(query).then((result) => callback(result));
}, 300);

const selectorStyles = (props) => {
  return {
    placeholder: (base) => ({
      ...base,
      fontSize: "medium",
      ..._.get(props, "customStyles.placeholder", {}),
    }),
    singleValue: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
      ..._.get(props, "customStyles.singleValue", {}),
    }),
    control: (base) => ({
      ...base,
      borderRadius: "5px",
      background: props.disabled ? "lightgray" : "white",
      ..._.get(props, "customStyles.control", {}),
    }),
    input: (base) => ({
      ...base,
      height: "2.4rem",
      alignContent: "center",
      color: jssVariables.colors.text.dark,
      ..._.get(props, "customStyles.input", {}),
    }),
    indicatorsContainer: () => ({
      display: "none",
      ..._.get(props, "customStyles.indicatorsContainer", {}),
    }),
  };
};
export const PortAutoComplete = ({
  placeholder,
  onSelect,
  disabled,
  defaultValue,
  customStyles,
}) => {
  var commonProps = {
    isDisabled: disabled,
    isClearable: true,

    defaultOptions: true,
    blurInputOnSelect: true,
    loadOptions: (query, callback) => debouncedSearch(query, callback),
    onChange: (item) => {
      if (!item) {
        return;
      }

      onSelect({
        code: [`${item.name}___${item.id}`],
        meta: item,
      });
    },
    placeholder,
  };
  if (!_.isEmpty(defaultValue)) {
    commonProps = { ...commonProps, value: defaultValue };
  }
  return (
    <AsyncSelect
      {...commonProps}
      menuPlacement="top"
      styles={selectorStyles({ disabled, customStyles })}
    />
  );
};
PortAutoComplete.defaultProps = {
  placeholder: "",
  defaultValue: {},
  disabled: false,
};
PortAutoComplete.propTypes = {
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  defaultValue: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const PortAutoCompleteComp = ({
  field,
  form: { touched, errors, setFieldValue },
  ...props
}) => {
  const { withErrors } = props;

  return (
    <React.Fragment>
      <PortAutoComplete
        onSelect={(port) => {
          setFieldValue(field.name, {
            [`${field.name}_meta`]: [port.meta],
          });
        }}
        {...props}
      />
      {withErrors && touched[field.name] && errors[field.name] && (
        <div className={props.styles.errorMessage}>{errors[field.name]}</div>
      )}
    </React.Fragment>
  );
};
export const PortAutoCompleteField = ({
  id,
  name,
  placeholder,
  label,
  disabled,
  defaultValue,
  withErrors = true,
  customStyles,
}) => {
  const classes = autoCompleteInputStyles({
    customStyles,
    hasLabel: Boolean(label),
  });

  return (
    <div className={classes.inputGroup} id={id}>
      <label className={classes.label}>{label}</label>
      <div className={classes.inputGroupBody}>
        <Field
          name={name}
          disabled={disabled}
          placeholder={<div className={classes.placeholder}>{placeholder}</div>}
          withErrors={withErrors}
          defaultValue={defaultValue}
          customStyles={customStyles}
          component={PortAutoCompleteComp}
        />
      </div>
    </div>
  );
};
PortAutoCompleteField.defaultProps = {
  name: "",
  label: "",
  types: [],
  placeholder: "",
  defaultValue: {},
  customStyles: {},
  id: "",
};
PortAutoCompleteField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  types: PropTypes.array,
  defaultValue: PropTypes.object,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  customStyles: PropTypes.object,
  id: PropTypes.string,
};
